/**
 * slotLogic — the pure half of the callback overbooking check and the visual
 * slot picker (see "Callback overbooking check and slot picker" in
 * `frontend/src/crm/CLAUDE.md`).
 *
 * Two owner asks ride on this file:
 *   1. When a recruiter picks a date and time for a callback, cross-check the
 *      recruiter's calendar and warn before it double-books them. The warning
 *      is overridable with one press (no note).
 *   2. A week grid of the recruiter's day with their other appointments on it,
 *      so a free spot can be found by eye and placed with a click.
 *
 * Everything here is pure (no api, no React), so the tests need no mocks. The
 * wire shapes are GET /api/crm/callbacks/availability and the 409
 * `{ code: 'overbooked', conflicts }` refusal from the callback writes.
 *
 * TIME ZONES. Times render in the browser's local zone, the same assumption the
 * callback card's `toLocalInput` makes; the booking window's HH:MM are read as
 * local wall-clock times too (the card's quick picks already do). The one place
 * the tenant's zone matters is `absent_days` / `closed_dates`, which the server
 * keys by the tenant-local date: `tenantDayKeyOf(timezone)` maps an instant to
 * that key.
 */

// ---------------------------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------------------------

/** One busy stretch on a recruiter's calendar (GET /callbacks/availability). */
export interface Busy {
  kind: 'callback' | 'block';
  start: string;
  end: string;
  /** 'Callback — Jane Doe' | the block's title on your own calendar | 'Busy' for a teammate's private block. */
  label: string;
  callback_id?: string;
  contact_id?: string;
  contact_name?: string | null;
  /** The block is on the viewer's own calendar (its title is shown). */
  own?: boolean;
}

/** One reason a time is taken, as the 409 `overbooked` refusal lists them. */
export interface Conflict {
  /** 'absent' = the recruiter is out of office that day (start/end null). */
  kind: 'callback' | 'block' | 'absent';
  start: string | null;
  end: string | null;
  label: string;
  callback_id?: string;
  contact_id?: string;
  contact_name?: string | null;
}

/** The tenant booking window as the availability read serves it. */
export interface BookingWindow {
  /** 0 = Sunday. */
  days: number[];
  day_start: string;
  day_end: string;
  slot_minutes: number;
  /** Tenant-local YYYY-MM-DD dates the office is closed. */
  closed_dates?: string[];
}

export interface AvailabilityResponse {
  assignee_user_id: string;
  timezone: string;
  booking: BookingWindow;
  busy: Busy[];
  /** Tenant-local YYYY-MM-DD days the recruiter is out of office. */
  absent_days: string[];
}

// ---------------------------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------------------------

/** The grid (and the form's minute dropdown) snap to quarter hours. */
export const SLOT_STEP_MIN = 15;
/** The grid never starts before 6 AM or ends after 9 PM, whatever the padding says. */
export const GRID_FLOOR_MIN = 6 * 60;
export const GRID_CEIL_MIN = 21 * 60;
/** A call length when the server has not said (the platform's booking default). */
export const DEFAULT_SLOT_MIN = 30;

export const FALLBACK_WINDOW: BookingWindow = {
  days: [1, 2, 3, 4, 5],
  day_start: '08:00',
  day_end: '17:00',
  slot_minutes: DEFAULT_SLOT_MIN,
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Monday-first display order of the weekdays (Sunday last). */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

// ---------------------------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------------------------

/** "HH:MM" -> minutes past midnight (NaN-safe: a bad value reads 0). */
export function minutesOf(hhmm: string | null | undefined): number {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : 0;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** A Date as a datetime-local value ("YYYY-MM-DDTHH:MM", local clock). */
export function toLocalInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "YYYY-MM-DDTHH:MM" -> a local Date, or null for anything else. */
export function parseLocalInput(value: string | null | undefined): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(String(value || ''));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Local midnight of a day. */
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** `n` calendar days later (DST-safe: steps the date, not 24h blocks). */
export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Monday 00:00 (local) of the week that holds `d`. */
export function startOfWeek(d: Date): Date {
  const back = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  return addDays(startOfDay(d), -back);
}

/** Local YYYY-MM-DD of an instant. */
export function localDayKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * A mapper from an instant to the TENANT-local YYYY-MM-DD (the key the server
 * uses for absent_days and closed_dates). Falls back to the browser's day when
 * the zone is missing or unknown to this browser.
 */
export function tenantDayKeyOf(timezone: string | null | undefined): (ms: number) => string {
  if (!timezone) return localDayKey;
  try {
    const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' });
    return (ms: number) => {
      const parts = fmt.formatToParts(new Date(ms));
      const get = (t: string) => parts.find((p) => p.type === t)?.value || '';
      const key = `${get('year')}-${get('month')}-${get('day')}`;
      return /^\d{4}-\d{2}-\d{2}$/.test(key) ? key : localDayKey(ms);
    };
  } catch {
    return localDayKey;
  }
}

/** The key of a whole local day column (read at local noon, so a zone offset never tips it). */
export function dayColumnKey(day: Date, dayKeyOf: (ms: number) => string): string {
  const noon = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 12);
  return dayKeyOf(noon.getTime());
}

/** The `from`/`to` ISO pair covering the local day of a "YYYY-MM-DDTHH:MM" value. */
export function localDayRange(value: string): { from: string; to: string } | null {
  const d = parseLocalInput(value);
  if (!d) return null;
  const from = startOfDay(d);
  return { from: from.toISOString(), to: addDays(from, 1).toISOString() };
}

/** The `from`/`to` ISO pair covering the Monday-to-Monday week that holds `d` (7 days, under the 14-day cap). */
export function weekRange(d: Date): { from: string; to: string } {
  const from = startOfWeek(d);
  return { from: from.toISOString(), to: addDays(from, 7).toISOString() };
}

/** Defensive read of the availability body: never trust arrays or the window to be there. */
export function normalizeAvailability(data: any): AvailabilityResponse {
  const b = data?.booking || {};
  const days = Array.isArray(b.days) && b.days.length ? b.days.map(Number).filter((n: number) => n >= 0 && n <= 6) : FALLBACK_WINDOW.days;
  const slot = Number(b.slot_minutes);
  return {
    assignee_user_id: String(data?.assignee_user_id || ''),
    timezone: typeof data?.timezone === 'string' ? data.timezone : '',
    booking: {
      days,
      day_start: typeof b.day_start === 'string' && b.day_start ? b.day_start : FALLBACK_WINDOW.day_start,
      day_end: typeof b.day_end === 'string' && b.day_end ? b.day_end : FALLBACK_WINDOW.day_end,
      slot_minutes: Number.isFinite(slot) && slot > 0 ? slot : DEFAULT_SLOT_MIN,
      closed_dates: Array.isArray(b.closed_dates) ? b.closed_dates.filter((s: unknown) => typeof s === 'string') : [],
    },
    busy: Array.isArray(data?.busy)
      ? data.busy.filter((x: any) => x && typeof x.start === 'string' && typeof x.end === 'string')
      : [],
    absent_days: Array.isArray(data?.absent_days) ? data.absent_days.filter((s: unknown) => typeof s === 'string') : [],
  };
}

// ---------------------------------------------------------------------------------------------
// The overlap rule
// ---------------------------------------------------------------------------------------------

/**
 * What a call of `durationMin` minutes starting at `startMs` would collide
 * with: every busy stretch that overlaps it (edges that only touch do NOT
 * overlap, so a 10:00–10:30 call sits fine after a 9:30–10:00 one), plus an
 * 'absent' conflict when the recruiter is out of office that (tenant) day.
 * The absent line sorts first, then the rest by start time.
 */
export function overlapsFor(
  busy: Busy[],
  absentDays: string[],
  startMs: number,
  durationMin: number,
  dayKeyOf: (ms: number) => string,
): Conflict[] {
  if (!Number.isFinite(startMs)) return [];
  const endMs = startMs + Math.max(1, durationMin || DEFAULT_SLOT_MIN) * 60_000;
  const out: Conflict[] = [];
  for (const b of busy) {
    const s = Date.parse(b.start);
    const e = Date.parse(b.end);
    if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
    if (s < endMs && e > startMs) {
      out.push({
        kind: b.kind,
        start: b.start,
        end: b.end,
        label: b.label,
        ...(b.callback_id ? { callback_id: b.callback_id } : {}),
        ...(b.contact_id ? { contact_id: b.contact_id } : {}),
        ...(b.contact_name !== undefined ? { contact_name: b.contact_name } : {}),
      });
    }
  }
  out.sort((a, b) => Date.parse(a.start as string) - Date.parse(b.start as string));
  if (absentDays.includes(dayKeyOf(startMs))) {
    out.unshift({ kind: 'absent', start: null, end: null, label: 'Out of office that day' });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------------------------

/** "10:30" + "AM" for a local instant. */
function clock12(d: Date): { text: string; mer: 'AM' | 'PM' } {
  const h = d.getHours();
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return { text: `${h12}:${pad(d.getMinutes())}`, mer: h >= 12 ? 'PM' : 'AM' };
}

/** "10:30 AM" (local). */
export function timeLabel(ms: number): string {
  const c = clock12(new Date(ms));
  return `${c.text} ${c.mer}`;
}

/** "9 AM" / "12 PM" for a whole-hour minute count (the grid's time column). */
export function hourLabel(minute: number): string {
  const h = Math.floor(minute / 60) % 24;
  return `${h % 12 === 0 ? 12 : h % 12} ${h >= 12 ? 'PM' : 'AM'}`;
}

/** "10:00–10:30 AM", or "11:30 AM–12:30 PM" across noon (local). */
export function timeRangeLabel(startMs: number, endMs: number): string {
  const a = clock12(new Date(startMs));
  const b = clock12(new Date(endMs));
  return a.mer === b.mer ? `${a.text}–${b.text} ${b.mer}` : `${a.text} ${a.mer}–${b.text} ${b.mer}`;
}

/** "Tue" (local). */
export function weekdayShort(d: Date): string {
  return WEEKDAYS[d.getDay()];
}

/** "Tue, Oct 6" (local). */
export function dayShortLabel(d: Date): string {
  return `${WEEKDAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** "Oct 5 – 9, 2026" or "Sep 28 – Oct 2, 2026" for a run of days. */
export function rangeLabel(first: Date, last: Date): string {
  const head = `${MONTHS[first.getMonth()]} ${first.getDate()}`;
  const tail = first.getMonth() === last.getMonth() ? `${last.getDate()}` : `${MONTHS[last.getMonth()]} ${last.getDate()}`;
  return first.getTime() === startOfDay(last).getTime() ? `${head}, ${last.getFullYear()}` : `${head} – ${tail}, ${last.getFullYear()}`;
}

/** One conflict as a line: "Callback — Jane Doe · 10:00–10:30 AM"; the absent line is its label alone. */
export function conflictLine(c: Conflict): string {
  if (c.kind === 'absent') return c.label || 'Out of office that day';
  const s = c.start ? Date.parse(c.start) : NaN;
  const e = c.end ? Date.parse(c.end) : NaN;
  const label = c.label || (c.kind === 'callback' ? 'Callback' : 'Busy');
  return Number.isFinite(s) && Number.isFinite(e) ? `${label} · ${timeRangeLabel(s, e)}` : label;
}

/** "Maria already has:" / "You already have:" — the alert's lead-in. */
export function conflictHeadline(name: string | null | undefined, isMe: boolean): string {
  if (isMe) return 'You already have:';
  return `${name || 'This recruiter'} already has:`;
}

/** "Tue, Oct 6 · 10:30–11:00 AM with Maria" — the picker's footer line. */
export function selectionLabel(startMs: number, durationMin: number, name?: string | null): string {
  const d = new Date(startMs);
  const range = timeRangeLabel(startMs, startMs + durationMin * 60_000);
  return `${dayShortLabel(d)} · ${range}${name ? ` with ${name}` : ''}`;
}

// ---------------------------------------------------------------------------------------------
// The grid
// ---------------------------------------------------------------------------------------------

export interface GridBounds {
  /** Minutes past local midnight of the first row. */
  startMin: number;
  /** Minutes past local midnight where the last row ends. */
  endMin: number;
  /** Quarter-hour rows between them. */
  rows: number;
}

/**
 * The visible stretch of a day: the business day with an hour of padding
 * either side, snapped out to whole hours and clamped to 6 AM – 9 PM.
 */
export function gridBounds(w: BookingWindow): GridBounds {
  let ds = minutesOf(w.day_start);
  let de = minutesOf(w.day_end);
  if (de <= ds) {
    ds = minutesOf(FALLBACK_WINDOW.day_start);
    de = minutesOf(FALLBACK_WINDOW.day_end);
  }
  let startMin = Math.max(GRID_FLOOR_MIN, Math.floor(ds / 60) * 60 - 60);
  let endMin = Math.min(GRID_CEIL_MIN, Math.ceil(de / 60) * 60 + 60);
  if (endMin <= startMin) {
    // A window entirely outside 6 AM – 9 PM: show the window itself.
    startMin = Math.floor(ds / 60) * 60;
    endMin = Math.ceil(de / 60) * 60;
  }
  return { startMin, endMin, rows: Math.round((endMin - startMin) / SLOT_STEP_MIN) };
}

/** Minutes past local midnight of `day` for an instant, clamped to [0, 1440]. */
export function minuteInDay(ms: number, day: Date): number {
  const key = localDayKey(startOfDay(day).getTime());
  const at = localDayKey(ms);
  if (at < key) return 0;
  if (at > key) return 24 * 60;
  const d = new Date(ms);
  return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
}

/**
 * Where a stretch sits in a day column, in minutes from the grid's first row:
 * `{ top, height }`, clipped to the visible bounds. Null when none of it shows.
 */
export function placeBlock(
  startMs: number,
  endMs: number,
  day: Date,
  bounds: GridBounds,
): { top: number; height: number } | null {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;
  const s = Math.max(minuteInDay(startMs, day), bounds.startMin);
  const e = Math.min(minuteInDay(endMs, day), bounds.endMin);
  if (e <= s) return null;
  return { top: s - bounds.startMin, height: e - s };
}

/** The instant a grid cell starts: `minute` past local midnight of `day`. */
export function slotStartMs(day: Date, minute: number): number {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), Math.floor(minute / 60), minute % 60).getTime();
}

/** Floor a minute count to the quarter-hour grid. */
export function snapMinutes(min: number, step = SLOT_STEP_MIN): number {
  return Math.floor(min / step) * step;
}

/** True when a local day is one of the tenant's closed dates (a holiday). */
export function isClosedDate(day: Date, w: BookingWindow, dayKeyOf: (ms: number) => string): boolean {
  return (w.closed_dates || []).includes(dayColumnKey(day, dayKeyOf));
}

/** True when the office does not book on a local day (its weekday is shut, or it is a closed date). */
export function isClosedDay(day: Date, w: BookingWindow, dayKeyOf: (ms: number) => string): boolean {
  if (!w.days.includes(day.getDay())) return true;
  return isClosedDate(day, w, dayKeyOf);
}

export function isAbsentDay(day: Date, absentDays: string[], dayKeyOf: (ms: number) => string): boolean {
  return absentDays.includes(dayColumnKey(day, dayKeyOf));
}

/** True when a call of `durationMin` at `minute` on `day` sits inside business hours on an open day. */
export function inBusinessHours(day: Date, minute: number, durationMin: number, w: BookingWindow, dayKeyOf: (ms: number) => string): boolean {
  if (isClosedDay(day, w, dayKeyOf)) return false;
  return minute >= minutesOf(w.day_start) && minute + durationMin <= minutesOf(w.day_end);
}

export interface GridCell {
  /** Minutes past local midnight. */
  minute: number;
  startMs: number;
  /** 'past' cells are not selectable; 'busy' ones are, behind the override. */
  state: 'free' | 'busy' | 'past';
  /** Inside the business day on an open day (outside is shaded; booking there asks to confirm). */
  inHours: boolean;
  conflicts: Conflict[];
}

/** Every quarter-hour cell of one day column, with what a call starting there would hit. */
export function dayCells(
  day: Date,
  bounds: GridBounds,
  w: BookingWindow,
  durationMin: number,
  busy: Busy[],
  absentDays: string[],
  dayKeyOf: (ms: number) => string,
  nowMs: number,
): GridCell[] {
  const out: GridCell[] = [];
  for (let minute = bounds.startMin; minute < bounds.endMin; minute += SLOT_STEP_MIN) {
    const startMs = slotStartMs(day, minute);
    const conflicts = overlapsFor(busy, absentDays, startMs, durationMin, dayKeyOf);
    out.push({
      minute,
      startMs,
      state: startMs < nowMs ? 'past' : conflicts.length ? 'busy' : 'free',
      inHours: inBusinessHours(day, minute, durationMin, w, dayKeyOf),
      conflicts,
    });
  }
  return out;
}

/** The booking weekdays, Monday first; the platform default when the window names none. */
function openWeekdays(w: BookingWindow): number[] {
  const open = WEEK_ORDER.filter((d) => w.days.includes(d));
  return open.length ? open : FALLBACK_WINDOW.days;
}

/** The columns of the week holding `anchor`: its booking days, Monday first (Sunday last). */
export function weekDays(anchor: Date, w: BookingWindow): Date[] {
  const monday = startOfWeek(anchor);
  return openWeekdays(w).map((wd) => addDays(monday, (wd + 6) % 7));
}

/** `anchor` itself when it is a booking weekday, else the next one forward. */
export function snapToBookingDay(anchor: Date, w: BookingWindow): Date {
  const open = openWeekdays(w);
  let d = startOfDay(anchor);
  for (let i = 0; i < 7 && !open.includes(d.getDay()); i++) d = addDays(d, 1);
  return d;
}

/** The next (dir 1) or previous (dir -1) booking weekday after `from` (the phone's day stepper). */
export function stepBookingDay(from: Date, dir: 1 | -1, w: BookingWindow): Date {
  const open = openWeekdays(w);
  let d = addDays(startOfDay(from), dir);
  for (let i = 0; i < 7 && !open.includes(d.getDay()); i++) d = addDays(d, dir);
  return d;
}

// ---------------------------------------------------------------------------------------------
// The write-side refusals and the override loop
// ---------------------------------------------------------------------------------------------

/** The conflicts of a 409 `{ code: 'overbooked' }` refusal, or null for any other failure. */
export function overbookConflictsOf(e: unknown): Conflict[] | null {
  const res = (e as { response?: { status?: number; data?: any } } | null)?.response;
  if (res?.status !== 409 || res?.data?.code !== 'overbooked') return null;
  return Array.isArray(res.data.conflicts) ? (res.data.conflicts as Conflict[]) : [];
}

/** The hours label of a 422 `{ code: 'outside_hours' }` refusal ('' when it names none), or null otherwise. */
export function outsideHoursOf(e: unknown): string | null {
  const res = (e as { response?: { status?: number; data?: any } } | null)?.response;
  if (res?.status !== 422 || res?.data?.code !== 'outside_hours') return null;
  return typeof res.data.hours_label === 'string' ? res.data.hours_label : '';
}

export interface OverrideFlags {
  outsideHours: boolean;
  overbook: boolean;
}

/**
 * Send a callback write, answering each server refusal once with the matching
 * confirm: 422 outside_hours (checked first by the server) and 409 overbooked,
 * in either order. Each flag flips at most once, so the loop ends. Resolves
 * null when the recruiter declines a confirm; rethrows every other failure.
 * `confirmOutsideHours` absent = a 422 is not overridable here (rethrown).
 */
export async function sendWithOverrides<T>(
  send: (flags: OverrideFlags) => Promise<T>,
  opts: {
    initial?: Partial<OverrideFlags>;
    confirmOutsideHours?: (hoursLabel: string) => Promise<boolean>;
    confirmOverbook: (conflicts: Conflict[]) => Promise<boolean>;
  },
): Promise<{ result: T; flags: OverrideFlags } | null> {
  const flags: OverrideFlags = { outsideHours: false, overbook: false, ...opts.initial };
  for (;;) {
    try {
      const result = await send({ ...flags });
      return { result, flags };
    } catch (e) {
      const hours = outsideHoursOf(e);
      if (hours !== null && !flags.outsideHours && opts.confirmOutsideHours) {
        if (!(await opts.confirmOutsideHours(hours))) return null;
        flags.outsideHours = true;
        continue;
      }
      const conflicts = overbookConflictsOf(e);
      if (conflicts && !flags.overbook) {
        if (!(await opts.confirmOverbook(conflicts))) return null;
        flags.overbook = true;
        continue;
      }
      throw e;
    }
  }
}
